import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { resolveTranslation } from '@/utils/resolveTranslation';
import { translateRespondentMedia } from '@/pages/projects/single-project/Content/project_results/resultsPageTranslations';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import ImageLightbox from '@/components/ui/ImageLightbox';

interface MediaItem {
  fileId: string;
  url: string;
  type: string;
  transcriptionText?: string;
}

interface ConversationMedia {
  conversationId: string;
  respondentIndex: number;
  media: MediaItem[];
}

interface RespondentMediaGalleryProps {
  data: ConversationMedia[];
  isLoading: boolean;
  analysisLanguage: string;
}

interface MediaThumbnailProps {
  item: {
    fileId: string;
    url: string;
    type: string;
    transcriptionText?: string;
    respondentIndex: number;
    conversationId: string;
  };
  showTranscription?: boolean;
  /** Called when the user clicks an image thumbnail. Wired up by the parent
   *  to open the gallery-wide lightbox at this item's index. Undefined for
   *  non-image items (videos play in place). */
  onImageClick?: () => void;
}

function MediaThumbnail({ item, showTranscription = false, onImageClick }: MediaThumbnailProps) {
  if (item.type === 'image') {
    // Image transcriptionText is the AI vision-model description of the
    // image content — not a respondent transcript. We deliberately do
    // NOT render it under the thumbnail (it's noisy and was a UX
    // regression). The field is still persisted on the backend because
    // the AI uses it as conversation context for follow-up questions.
    return (
      <button
        type="button"
        onClick={onImageClick}
        className="block w-full p-0 border-0 bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-lg"
      >
        <img
          src={item.url}
          alt={`Respondent #${item.respondentIndex}`}
          className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition-opacity cursor-pointer"
        />
      </button>
    );
  }

  if (item.type === 'video') {
    return (
      <div className="flex flex-col gap-1">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          src={item.url}
          controls
          preload="metadata"
          className="w-full h-32 object-cover rounded-lg border border-gray-200"
        />
        {showTranscription && item.transcriptionText && (
          <p className="text-xs text-gray-500 line-clamp-2">{item.transcriptionText}</p>
        )}
      </div>
    );
  }

  return null;
}

function RespondentMediaGallery({
  data,
  isLoading,
  analysisLanguage,
}: RespondentMediaGalleryProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const { open: openTranscriptPanel } = useTranscriptPanelStore();

  const totalMedia = data.reduce((sum, c) => sum + c.media.length, 0);

  // Build a single, gallery-wide list of images for the lightbox.
  // Image indices are stable: each <MediaThumbnail> for an image gets
  // its global imageIndex prop which it passes back via onImageClick.
  const allImages = useMemo(
    () =>
      data.flatMap((c) =>
        c.media
          .filter((m) => m.type === 'image')
          .map((m) => ({
            url: m.url,
            respondent_index: c.respondentIndex,
          })),
      ),
    [data],
  );

  const openLightbox = (idx: number) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  if (!isLoading && totalMedia === 0) return null;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-32 rounded-lg" />
          <Skeleton className="h-32 rounded-lg" />
          <Skeleton className="h-32 rounded-lg" />
        </div>
      </div>
    );
  }

  // Flatten all media items for the collapsed preview grid
  const allMedia = data.flatMap((c) =>
    c.media.map((m) => ({
      ...m,
      respondentIndex: c.respondentIndex,
      conversationId: c.conversationId,
    })),
  );

  const previewItems = allMedia.slice(0, 6);
  const remainingCount = allMedia.length - 6;

  return (
    <div className="flex flex-col w-full">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 mb-4"
      >
        <h3 className="text-sm font-semibold text-neutral-900">
          {resolveTranslation(translateRespondentMedia, analysisLanguage)}
        </h3>
        {/* TELV1-296 #5b: the count is media uploaded WHILE answering this
            question — not all media the respondent ever shared. Without the
            "in this question" qualifier, researchers compared the count
            against the full conversation transcript (which includes media
            from every other question) and reported a mismatch (e.g. 3 here
            vs 15 in the conversation panel). */}
        <span className="text-sm text-gray-500">
          ({totalMedia} {totalMedia === 1 ? 'item' : 'items'} in this question from {data.length}{' '}
          {data.length === 1 ? 'respondent' : 'respondents'})
        </span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </button>

      {/* Preview grid (collapsed state) */}
      {!isExpanded && (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {previewItems.map((item) => {
            const imageIdx =
              item.type === 'image' ? allImages.findIndex((img) => img.url === item.url) : -1;
            return (
              <MediaThumbnail
                key={item.fileId}
                item={item}
                onImageClick={imageIdx >= 0 ? () => openLightbox(imageIdx) : undefined}
              />
            );
          })}
          {remainingCount > 0 && (
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="flex items-center justify-center h-32 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
            >
              +{remainingCount} more
            </button>
          )}
        </div>
      )}

      {/* Full expanded view grouped by respondent */}
      {isExpanded && (
        <div className="flex flex-col gap-6">
          {data.map((conversation) => (
            <div key={conversation.conversationId} className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => openTranscriptPanel(conversation.conversationId)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
              >
                <Users className="h-4 w-4" />
                <span className="font-medium hover:underline">
                  Respondent #{conversation.respondentIndex}
                </span>
                <span className="text-gray-400">
                  ({conversation.media.length} {conversation.media.length === 1 ? 'item' : 'items'}{' '}
                  in this question)
                </span>
              </button>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {conversation.media.map((item) => {
                  const imageIdx =
                    item.type === 'image' ? allImages.findIndex((img) => img.url === item.url) : -1;
                  return (
                    <MediaThumbnail
                      key={item.fileId}
                      item={{
                        ...item,
                        respondentIndex: conversation.respondentIndex,
                        conversationId: conversation.conversationId,
                      }}
                      showTranscription
                      onImageClick={imageIdx >= 0 ? () => openLightbox(imageIdx) : undefined}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {allImages.length > 0 && (
        <ImageLightbox
          images={allImages}
          initialIndex={lightboxIndex}
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
        />
      )}
    </div>
  );
}

export default RespondentMediaGallery;
